import classNames from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';

import MoneyToggle from '@swf/components/MoneyToggle/MoneyToggle';
import { select } from '@swf/core/store';

import MoneyIcon from '../../assets/money-icon.svg';

import {
  arrow,
  moneyButton,
  playMoney,
  popper,
  realMoney,
  textColor
} from './PlayMoneyToggle.scss';

interface Props {
  className?: string;
}

const PlayMoneyToggle: React.FC<Props> = ({ className }) => {
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const [popperOpen, setPopperOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const modifiers = useMemo(
    () => [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'viewport'
        }
      },
      {
        name: 'offset',
        options: {
          offset: ({ placement }: { placement: string }) => [0, placement === 'bottom' ? 20 : 40]
        }
      }
    ],
    [arrowElement]
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers
  });

  // TODO: make hook and reuse in Search e.t.c.
  const handleClickOutside = useCallback(
    (e: Event) => {
      if (
        referenceElement?.contains(e.target as Node) ||
        popperElement?.contains(e.target as Node)
      ) {
        return;
      }
      setPopperOpen(false);
    },
    [popperElement, referenceElement]
  );

  useEffect(() => {
    window.addEventListener('click', handleClickOutside, true);
    return () => window.removeEventListener('click', handleClickOutside, true);
  }, [handleClickOutside]);

  return (
    <>
      <span
        className={moneyButton as string}
        ref={setReferenceElement}
        onClick={() => setPopperOpen(!popperOpen)}
      >
        <MoneyIcon className={isPlayMoney ? playMoney : realMoney} />
      </span>
      {popperOpen && (
        <div
          ref={setPopperElement}
          style={styles.popper}
          className={classNames(className, popper, textColor)}
          {...attributes.popper}
        >
          <div className={arrow} ref={setArrowElement} style={styles.arrow} />
          <MoneyToggle onOffToggle className={classNames(styles.active)} />
        </div>
      )}
    </>
  );
};

PlayMoneyToggle.displayName = 'PlayMoneyToggle';

export default PlayMoneyToggle;
